import { useState } from "react"
import { motion } from "motion/react"
import { ArrowRight, Check, Download01, FileCheck02 } from "@untitledui/icons"
import { toast } from "sonner"
import type { ActivationView } from "@/lib/api/onboarding-activation"
import { downloadSignedPdf } from "./signed-pdf"
import { PrimaryButton } from "./ui"
import { cx } from "@/lib/utils/cx"

// The moment after signing — the signature was the key, and this is the
// ignition: on a deep pine stage, the eTX core comes ONLINE for their shop.
// Viewfinder brackets lock around the mark, radar rings pulse outward,
// "Intelligence activated" — the brand's promise, delivered as a feeling.
// Below, the signed copy as a plain file receipt, then one full-width CTA —
// everything on the same column edges.
export function SignedStep({
  data,
  onContinue,
}: {
  data: ActivationView
  onContinue: () => void
}) {
  const [downloading, setDownloading] = useState(false)

  const today = new Date().toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  })

  const download = async () => {
    if (downloading) return
    setDownloading(true)
    try {
      await downloadSignedPdf(data)
    } catch {
      toast.error("The PDF couldn't be generated — try again.")
    } finally {
      setDownloading(false)
    }
  }

  const slug = data.shop.legalBusinessName.replace(/[^\w]+/g, "-").replace(/^-+|-+$/g, "")

  const brackets = [
    { cls: "left-0 top-0 border-l-2 border-t-2 rounded-tl-[2px]", x: -22, y: -22 },
    { cls: "right-0 top-0 border-r-2 border-t-2 rounded-tr-[2px]", x: 22, y: -22 },
    { cls: "bottom-0 left-0 border-b-2 border-l-2 rounded-bl-[2px]", x: -22, y: 22 },
    { cls: "bottom-0 right-0 border-b-2 border-r-2 rounded-br-[2px]", x: 22, y: 22 },
  ]

  return (
    <div className="py-2">
      {/* the activation stage */}
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 24 }}
        className="relative overflow-hidden rounded-2xl px-6 py-8 text-center"
        style={{ backgroundColor: "#0B2E22" }}
      >
        {/* atmosphere */}
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute -right-16 -top-20 size-[240px] rounded-full bg-emerald-500/20 blur-3xl" />
          <div className="absolute -bottom-24 -left-12 size-[220px] rounded-full bg-emerald-400/10 blur-3xl" />
        </div>

        {/* signed — the key turned */}
        <motion.p
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.35 }}
          className="relative flex items-center justify-center gap-2 text-[13px] font-medium text-emerald-300"
        >
          <span className="grid size-[18px] place-items-center rounded-full bg-emerald-400">
            <Check className="size-3 text-[#0B2E22]" strokeWidth={4} aria-hidden />
          </span>
          Data authorization signed · {data.agreement.signerName} · {today}
        </motion.p>

        {/* the core coming online */}
        <div className="relative mx-auto mt-7 grid size-[124px] place-items-center">
          {/* radar pulses */}
          {[0, 1, 2].map((i) => (
            <motion.span
              key={i}
              aria-hidden
              initial={{ scale: 0.45, opacity: 0 }}
              animate={{ scale: 2.5, opacity: [0, 0.55, 0] }}
              transition={{ delay: 0.85 + i * 0.4, duration: 1.2, ease: "easeOut" }}
              className="absolute inset-0 rounded-full border border-emerald-400/70"
            />
          ))}
          {/* viewfinder locks on */}
          {brackets.map((b) => (
            <motion.span
              key={b.cls}
              aria-hidden
              initial={{ opacity: 0, x: b.x, y: b.y }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ type: "spring", stiffness: 320, damping: 20, delay: 0.45 }}
              className={cx("absolute size-4 border-emerald-400", b.cls)}
            />
          ))}
          {/* the mark, powered */}
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 360, damping: 15, delay: 0.6 }}
            className="relative grid place-items-center"
          >
            <span
              aria-hidden
              className="absolute size-16 rounded-full bg-emerald-400/30 blur-xl"
            />
            <span
              className="relative text-[34px] font-black leading-none tracking-tight text-white"
              style={{ textShadow: "0 0 24px rgba(52,211,153,0.55)" }}
            >
              eTX
            </span>
          </motion.div>
        </div>

        <motion.h2
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.45 }}
          className="relative mt-6 text-[23px] font-bold leading-tight text-white"
        >
          Intelligence activated
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.25, duration: 0.4 }}
          className="relative mt-1 text-[13.5px] text-white/60"
        >
          for {data.shop.name}
        </motion.p>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.45, duration: 0.5 }}
          className="relative mt-3 text-[8.5px] font-semibold uppercase tracking-[0.3em] text-emerald-300"
        >
          Collision · AI · Intelligence
        </motion.p>
      </motion.div>

      {/* the signed copy — a plain file receipt, same column edges */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.6, duration: 0.4 }}
        className="mt-4 flex flex-col gap-3 rounded-xl border border-[#E5EAE7] bg-white px-4 py-3.5 sm:flex-row sm:items-center"
      >
        <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-emerald-50">
          <FileCheck02 className="size-5 text-emerald-600" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13.5px] font-semibold text-[#0C2018]">
            eTX-Data-Authorization-{slug}.pdf
          </p>
          <p className="text-[12px] text-[#96A29A]">Your signed copy — keep it with your records</p>
        </div>
        <button
          type="button"
          onClick={download}
          disabled={downloading}
          className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-lg border border-[#D5DDD8] bg-white px-4 text-[13.5px] font-semibold text-[#0C2018] transition-colors hover:bg-[#F4F6F5] disabled:opacity-50"
        >
          <Download01 className="size-4 text-emerald-600" aria-hidden />
          {downloading ? "Preparing…" : "Download"}
        </button>
      </motion.div>

      {/* one full-width action — flush with the panels above */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.75, duration: 0.4 }}
        className="mt-4"
      >
        <PrimaryButton onClick={onContinue} className="w-full">
          Choose your call time
          <ArrowRight className="size-4" />
        </PrimaryButton>
      </motion.div>
    </div>
  )
}
